import { useState, useRef, useEffect, useMemo } from "react";
import { useMultiSedeStore } from "../../../../stores/useMultiSedeStore";
import { sortLocalsCentralFirst } from "../../../../utils/sortLocalsCentralFirst";
import styles from "./LocalSwitcher.module.css";

const LocalSwitcher = () => {
  const {
    isCorporate,
    isCentralUser,
    accessibleLocals: rawAccessibleLocals,
    selectedLocalId,
    selectedLocalName,
    isLoading,
    switchLocal,
  } = useMultiSedeStore();

  const accessibleLocals = useMemo(() => sortLocalsCentralFirst(rawAccessibleLocals), [rawAccessibleLocals]);

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Si el usuario no puede cambiar de sede (mono-sede o no-corporativo),
  // mostrar solo un indicador read-only con el nombre del local actual.
  if (!isCorporate || accessibleLocals.length <= 1) {
    if (!selectedLocalName) return null;
    return (
      <div className={styles.container} data-testid="local-indicator">
        <div className={`${styles.trigger} ${styles.triggerStatic}`}>
          <span className={styles.icon}>&#x1F4CD;</span>
          <span className={styles.label}>{selectedLocalName}</span>
        </div>
      </div>
    );
  }

  const handleSwitch = async (localId: number) => {
    if (localId === selectedLocalId) {
      setIsOpen(false);
      return;
    }
    const success = await switchLocal(localId);
    if (success) {
      setIsOpen(false);
      // Defer el reload al siguiente tick de macrotask para garantizar que
      // el setItem de localStorage haya hecho commit antes de que el browser
      // empiece a cargar la página nueva. Sin este defer, en algunos browsers
      // el reload arrancaba con el token viejo y forzaba re-login.
      setTimeout(() => window.location.reload(), 50);
    }
  };

  const displayName = selectedLocalName || "Seleccionar local";
  const isCurrentCentral = accessibleLocals.find(l => l.id === selectedLocalId)?.isCentral;

  return (
    <div className={styles.container} ref={dropdownRef}>
      <button
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
      >
        <span className={styles.icon}>&#x1F4CD;</span>
        <span className={styles.label}>
          {isLoading ? "Cambiando..." : displayName}
        </span>
        {isCurrentCentral && (
          <span style={{ fontSize: 9, fontWeight: 700, color: "#fff", background: "#16a34a", borderRadius: 10, padding: "1px 6px", flexShrink: 0 }}>
            Central
          </span>
        )}
        <span className={styles.chevron}>{isOpen ? "\u25B2" : "\u25BC"}</span>
      </button>
      {isOpen && (
        <div className={styles.dropdown}>
          {accessibleLocals.map((local) => (
            <button
              key={local.id}
              className={`${styles.option} ${local.id === selectedLocalId ? styles.optionActive : ""}`}
              onClick={() => handleSwitch(local.id)}
            >
              <span className={styles.optionIcon}>&#x1F4CD;</span>
              <span>{local.name}</span>
              {local.isCentral && (
                <span style={{ fontSize: 9, fontWeight: 700, color: "#fff", background: "#16a34a", borderRadius: 10, padding: "1px 6px", marginLeft: 4, flexShrink: 0 }}>
                  Central
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalSwitcher;
